import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { Loader2, Plus, FileText, Clock, CheckCircle, XCircle, Wallet, Info } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatCurrency, formatDateTime, cn } from "../lib/utils";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const employmentStatuses = ["Employed", "Self-Employed", "Unemployed", "Student", "Retired"];
const loanPurposes = ["Personal", "Business", "Education", "Medical", "Home Improvement", "Other"];

export const Loans = () => {
  const { user } = useAuth();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    loanAmount: "",
    loanPurpose: "",
    monthlyIncome: "",
    employmentStatus: "",
  });

  useEffect(() => { fetchLoans(); }, []);

  const fetchLoans = async () => {
    try {
      const response = await axios.get(`${API_URL}/loans`);
      setLoans(response.data);
    } catch (error) {
      console.error("Failed to fetch loans:", error);
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.loanAmount || !formData.loanPurpose || !formData.monthlyIncome || !formData.employmentStatus) {
      toast.error("Please fill all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/loans/apply`, {
        loan_amount: parseFloat(formData.loanAmount),
        loan_purpose: formData.loanPurpose,
        monthly_income: parseFloat(formData.monthlyIncome),
        employment_status: formData.employmentStatus,
      });
      toast.success("Application Submitted Successfully");
      setIsOpen(false);
      setFormData({ loanAmount: "", loanPurpose: "", monthlyIncome: "", employmentStatus: "" });
      fetchLoans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit application");
    } finally { setSubmitting(false); }
  };

  const totalApproved = loans
    .filter(l => l.status === 'approved')
    .reduce((sum, l) => sum + Number(l.loan_amount), 0);

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header & Stats */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Loans & Credit</h1>
          <p className="text-slate-500 mt-1 text-sm">Manage your applications and credit facility.</p>
        </div>
        
        <div className="flex items-center gap-6 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex flex-col">
                <span className="text-[12px] font-black uppercase text-slate-400 tracking-wider">Total Active Credit</span>
                <span className="text-sm font-bold text-primary">{formatCurrency(totalApproved, user?.currency)}</span>
            </div>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                    <Button className="rounded-xl h-11 px-4 shadow-lg shadow-primary/20 text-sm">
                        <Plus className="w-4 h-4 mr-2" /> Apply Now
                    </Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-md border-l-0 shadow-2xl">
                    <SheetHeader className="pb-6 border-b border-slate-50">
                        <SheetTitle className="text-2xl font-bold">Loan Application</SheetTitle>
                        <SheetDescription>Provide your details for a quick credit assessment.</SheetDescription>
                    </SheetHeader>
                    <form onSubmit={handleSubmit} className="space-y-6 pt-8">
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-[11px] font-bold uppercase text-slate-500 ml-1">Loan Amount</Label>
                                <Input 
                                    type="number" 
                                    className="h-12 rounded-xl bg-slate-50/50 border-slate-100 focus:border-primary transition-all"
                                    value={formData.loanAmount}
                                    onChange={(e) => setFormData(p => ({ ...p, loanAmount: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-[11px] font-bold uppercase text-slate-500 ml-1">Monthly Income</Label>
                                <Input 
                                    type="number" 
                                    className="h-12 rounded-xl bg-slate-50/50 border-slate-100 focus:border-primary transition-all"
                                    value={formData.monthlyIncome}
                                    onChange={(e) => setFormData(p => ({ ...p, monthlyIncome: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-[11px] font-bold uppercase text-slate-500 ml-1">Loan Purpose</Label>
                                <Select onValueChange={(v) => setFormData(p => ({ ...p, loanPurpose: v }))}>
                                    <SelectTrigger className="h-12 rounded-xl bg-slate-50/50 border-slate-100">
                                        <SelectValue placeholder="Select purpose" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {loanPurposes.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-[11px] font-bold uppercase text-slate-500 ml-1">Employment Status</Label>
                                <Select onValueChange={(v) => setFormData(p => ({ ...p, employmentStatus: v }))}>
                                    <SelectTrigger className="h-12 rounded-xl bg-slate-50/50 border-slate-100">
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {employmentStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <Button type="submit" className="w-full h-12 rounded-xl text-md font-bold" disabled={submitting}>
                            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Application"}
                        </Button>
                    </form>
                </SheetContent>
            </Sheet>
        </div>
      </div>

      {/* Main List */}
      <Card className="border-none shadow-none bg-transparent">
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse" />)}
            </div>
          ) : loans.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-slate-300" />
              </div>
              <p className="text-slate-500 font-medium">No applications found</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {loans.map((loan) => (
                <div key={loan.id} className="group flex items-center justify-between p-5 bg-white rounded-2xl border border-slate-100 hover:border-primary/20 hover:shadow-md transition-all duration-300">
                  <div className="flex items-center gap-5">
                    <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center",
                        loan.status === 'approved' ? 'bg-green-50 text-green-600' : 
                        loan.status === 'rejected' ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-600'
                    )}>
                        {loan.status === 'approved' ? <CheckCircle className="w-6 h-6" /> : 
                         loan.status === 'rejected' ? <XCircle className="w-6 h-6" /> : <Clock className="w-6 h-6" />}
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 leading-tight">
                        {formatCurrency(loan.loan_amount, user?.currency)}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[12px] font-black uppercase text-slate-400 tracking-tighter bg-slate-50 px-2 py-0.5 rounded">
                            {loan.loan_purpose}
                        </span>
                        <span className="text-[12px] text-slate-400 font-medium">
                            Applied {formatDateTime(loan.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <Badge className={cn(
                        "rounded-lg px-3 py-1 text-[8px] uppercase font-black tracking-widest border-none",
                        loan.status === 'approved' ? 'bg-green-100 text-green-700' : 
                        loan.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                    )}>
                        {loan.status}
                    </Badge>
                    {loan.admin_notes && (
                        <div className="flex items-center gap-1.5 text-slate-400">
                            <Info className="w-3 h-3" />
                            <p className="text-[11px] font-medium max-w-[180px] truncate italic">
                                {loan.admin_notes}
                            </p>
                        </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};